import React from "react";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  orders: any[];
  selectedOrders: string[];
  setSelectedOrders: React.Dispatch<React.SetStateAction<string[]>>;
};

const getStatusBadge = (status: string) => {
  const normStatus = (status || "").toUpperCase();
  switch (normStatus) {
    case "NEW_ORDER":
      return {
        label: "New Order",
        className: "bg-emerald-50 border border-emerald-300 text-emerald-800",
      };
    case "ORDER_GENERATED":
      return {
        label: "Order Generated",
        className: "bg-teal-50 border border-teal-300 text-teal-800",
      };
    case "ORDER_CREATED":
      return {
        label: "Order Created",
        className: "bg-sky-50 border border-sky-300 text-sky-800",
      };
    case "PROCESSING":
      return {
        label: "Processing",
        className: "bg-indigo-50 border border-indigo-300 text-indigo-800",
      };
    case "READY_FOR_PICKUP":
    case "READY_TO_PICK":
      return {
        label: "Ready For Pickup",
        className: "bg-yellow-50 border border-yellow-300 text-yellow-800",
      };
    case "PACKED":
      return {
        label: "Packed",
        className: "bg-violet-50 border border-violet-300 text-violet-800",
      };
    case "DISPATCHED":
      return {
        label: "Dispatched",
        className: "bg-fuchsia-50 border border-fuchsia-300 text-fuchsia-800",
      };
    case "IN_TRANSIT":
      return {
        label: "In Transit",
        className: "bg-cyan-50 border border-cyan-300 text-cyan-800",
      };
    case "OUT_FOR_DELIVERY":
      return {
        label: "Out For Delivery",
        className: "bg-orange-50 border border-orange-300 text-orange-800",
      };
    case "DELIVERED":
      return {
        label: "Delivered",
        className: "bg-lime-50 border border-lime-300 text-lime-800",
      };
    case "UNDELIVERED":
      return {
        label: "Undelivered",
        className: "bg-amber-50 border border-amber-300 text-amber-800",
      };
    case "RETURNED":
      return {
        label: "Not Delivered",
        className: "bg-rose-50 border border-rose-300 text-rose-800",
      };
    case "REVERSE_NEW":
      return {
        label: "Reverse New",
        className: "bg-pink-50 border border-pink-300 text-pink-800",
      };
    case "DELETED":
      return {
        label: "Deleted",
        className: "bg-rose-50 border border-rose-300 text-rose-800",
      };
    case "FAILED":
      return {
        label: "Failed",
        className: "bg-red-50 border border-red-300 text-red-800",
      };
    case "LOST":
      return {
        label: "Lost",
        className: "bg-stone-50 border border-stone-300 text-stone-700",
      };
    case "DRAFT":
      return {
        label: "Draft",
        className: "bg-zinc-50 border border-zinc-300 text-zinc-700",
      };
    case "CANCELLED":
      return {
        label: "Cancelled",
        className: "bg-slate-50 border border-slate-300 text-slate-700",
      };
    case "RECEIVE_IN_WAREHOUSE":
    case "RECEIVED_IN_WAREHOUSE":
      return {
        label: "Receive In Warehouse",
        className: "bg-blue-50 border border-blue-300 text-blue-800",
      };
    case "PICKUP_GENERATED":
      return {
        label: "Pickup Generated",
        className: "bg-green-50 border border-green-300 text-green-800",
      };
    case "RETURN_TO_SHIPPER":
      return {
        label: "Return To Shipper",
        className: "bg-rose-100 border border-rose-300 text-rose-800",
      };
    case "FORWARD_TO_3PL":
      return {
        label: "Forward To 3PL",
        className: "bg-purple-100 border border-purple-300 text-purple-800",
      };
    case "DISPATCHED_TO_3PL":
      return {
        label: "Dispatched To 3PL",
        className: "bg-violet-100 border border-violet-300 text-violet-800",
      };
    case "PICKUP_COLLECTED":
      return {
        label: "Pickup Collected",
        className: "bg-teal-100 border border-teal-300 text-teal-800",
      };
    case "PICKLIST_GENERATED":
      return {
        label: "Picklist Generated",
        className: "bg-indigo-100 border border-indigo-300 text-indigo-800",
      };
    case "PICKED":
      return {
        label: "Picked",
        className: "bg-sky-100 border border-sky-300 text-sky-800",
      };
    case "RECEIVE_INBOUND":
      return {
        label: "Receive Inbound",
        className: "bg-cyan-100 border border-cyan-300 text-cyan-800",
      };
    case "SHELVE":
      return {
        label: "Shelve",
        className: "bg-emerald-100 border border-emerald-300 text-emerald-800",
      };
    case "PENDING":
      return {
        label: "Pending",
        className: "bg-amber-100 border border-amber-300 text-amber-800",
      };
    case "PENDING_STOCK":
      return {
        label: "Pending Stock",
        className: "bg-orange-100 border border-orange-300 text-orange-800",
      };
    default:
      const label = status
        ? status
            .split("_")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" ")
        : "Unknown";
      return {
        label,
        className: "bg-slate-50 border border-slate-200 text-slate-600",
      };
  }
};

const ProductTable = ({
  orders = [],
  selectedOrders = [],
  setSelectedOrders,
}: Props) => {
  const { t } = useLanguage();
  const maxStatusWidth = React.useMemo(() => {
    if (!orders?.length) return "60px";
    const maxLen = orders.reduce((max, item) => {
      const badge = getStatusBadge(item.status);
      return badge.label.length > max ? badge.label.length : max;
    }, 0);
    const calculatedWidth = Math.max(50, maxLen * 6.2 + 20);
    return `${calculatedWidth}px`;
  }, [orders]);

  // 🔹 Select All Handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedOrders(orders.map((order) => order.id.toString()));
    } else {
      setSelectedOrders([]);
    }
  };

  // 🔹 Select Single Order
  const handleSelectOrder = (
    e: React.ChangeEvent<HTMLInputElement>,
    orderId: string,
  ) => {
    if (e.target.checked) {
      setSelectedOrders((prev) => [...prev, orderId]);
    } else {
      setSelectedOrders((prev) => prev.filter((id) => id !== orderId));
    }
  };

  return (
    <div className="overflow-x-auto w-full">
      <table className="w-full text-sm border-collapse min-w-0 table-auto">
        {/* <colgroup>
          <col style={{ width: "6%" }} />
          <col style={{ width: "28%" }} />
          <col style={{ width: "28%" }} />
          <col style={{ width: "38%" }} />
        </colgroup> */}
        <thead>
          <tr className="bg-gray-100 border-b border-gray-200">
            <th className="py-2 px-2 text-left w-10">
              <input
                type="checkbox"
                onChange={handleSelectAll}
                checked={
                  orders.length > 0 && selectedOrders.length === orders.length
                }
                className="rounded text-[#FCB910] focus:ring-[#FCB910]"
              />
            </th>
            <th className="py-2 px-2 text-left font-semibold text-gray-700 text-xs">
              {t("AWB")}
            </th>
            {/* <th className="hidden sm:table-cell py-2 px-2 text-left font-semibold text-gray-700 text-xs">
             Origin
            </th>
            <th className="hidden sm:table-cell py-2 px-2 text-left font-semibold text-gray-700 text-xs">
              Destination
            </th> */}
            {/* <th className="py-2 px-2 text-center font-semibold text-gray-700 text-xs">
              Status
            </th> */}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {orders.map((item) => (
            <tr key={item.id} className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-2 text-left">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(item.id.toString())}
                    onChange={(e) => handleSelectOrder(e, item.id.toString())}
                    className="rounded text-[#FCB910] focus:ring-[#FCB910]"
                  />
                </label>
              </td>

              <td
                className="py-3 px-2 text-left font-medium text-black text-xs truncate"
                title={item.awbNumber}
              >
                {item.awbNumber}
              </td>

              {/* <td
                className="hidden sm:table-cell py-3 px-2 text-left text-[11px] text-gray-600 truncate"
                title={item.senderCityName}
              >
                {item.senderCityName}
              </td>
              <td
                className="hidden sm:table-cell py-3 px-2 text-left text-[11px] text-gray-600 truncate"
                title={item.receiverCityName}
              >
                {item.receiverCityName}
              </td> */}

              {/* <td className="py-3 px-1 text-center">
                {(() => {
                  const badge = getStatusBadge(item.status);
                  return (
                    <span
                      style={{ width: maxStatusWidth }}
                      className={`inline-block text-center border px-1.5 py-0.5 rounded-full text-[9px] font-bold truncate max-w-full align-middle ${badge.className}`}
                      title={badge.label}
                    >
                      {badge.label}
                    </span>
                  );
                })()}
              </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductTable;
